import { Dispatch } from "redux";
import { setLoading } from "./global";
import QuestService from "service/Quest";
import { showMessage } from "utilities/helper";
import {
  QuestUnitPayloadTypes,
  QuestMissionPayloadTypes,
  QuestCheckpointPayloadTypes,
  QuestBadgePayloadTypes,
} from "utilities/types/Quest";

const handleError = (dispatch: Dispatch, err: any) => {
  dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
  showMessage(
    !err.response
      ? err.message
      : !!err.response?.data?.error
      ? err.response?.data?.error
      : err.response?.data?.message,
    "error"
  );
};

const toArray = (data: any, key?: string): any[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.data?.data)) return data.data.data;
  if (key && Array.isArray(data[key])) return data[key];
  if (key && Array.isArray(data.data?.[key])) return data.data[key];
  if (key && Array.isArray(data[key]?.data)) return data[key].data;
  return [];
};

// ─── Units ────────────────────────────────────────────────────────────────────

export const getAllQuestUnits =
  (params?: { is_active?: boolean; search?: string }) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const data = await QuestService.getAllUnits(params);
      dispatch(setLoading(false));
      dispatch({ type: "SET_QUEST_UNITS", value: toArray(data, "units") });
    } catch (err: any) {
      dispatch(setLoading(false));
      if (err.request?.status !== 401) {
        showMessage(
          !err.response ? err.message : err.response?.data?.message ?? err.response?.data?.error,
          "error"
        );
      }
    }
  };

export const storeQuestUnit =
  (form: QuestUnitPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.storeUnit(form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getAllQuestUnits() as any);
      handleCloseModal();
      showMessage(data.message ?? "Unit berhasil ditambahkan", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const updateQuestUnit =
  (unitId: string, form: QuestUnitPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.updateUnit(unitId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getAllQuestUnits() as any);
      handleCloseModal();
      showMessage(data.message ?? "Unit berhasil diperbarui", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const deleteQuestUnit =
  (unitId: string, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.deleteUnit(unitId);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getAllQuestUnits() as any);
      handleCloseModal();
      showMessage(data.message ?? "Unit berhasil dihapus", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

// ─── Missions ─────────────────────────────────────────────────────────────────

export const getAllQuestMissions =
  (params?: { master_unit_id?: string | number; is_active?: boolean }) =>
  async (dispatch: Dispatch) => {
    dispatch(setLoading(true));
    try {
      const data = await QuestService.getAllMissions(params);
      dispatch(setLoading(false));
      dispatch({ type: "SET_QUEST_MISSIONS", value: toArray(data, "missions") });
      if (params?.master_unit_id) {
        dispatch({ type: "SET_QUEST_SELECTED_UNIT", value: String(params.master_unit_id) });
      }
    } catch (err: any) {
      dispatch(setLoading(false));
      if (err.request?.status !== 401) {
        showMessage(
          !err.response ? err.message : err.response?.data?.message ?? err.response?.data?.error,
          "error"
        );
      }
    }
  };

export const storeQuestMission =
  (form: QuestMissionPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch, getState: any) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.storeMission(form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      const { selectedUnitId } = getState().questReducer;
      dispatch(getAllQuestMissions(selectedUnitId ? { master_unit_id: selectedUnitId } : undefined) as any);
      handleCloseModal();
      showMessage(data.message ?? "Quest berhasil ditambahkan", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const updateQuestMission =
  (missionId: string, form: QuestMissionPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch, getState: any) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.updateMission(missionId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      const { selectedUnitId } = getState().questReducer;
      dispatch(getAllQuestMissions(selectedUnitId ? { master_unit_id: selectedUnitId } : undefined) as any);
      handleCloseModal();
      showMessage(data.message ?? "Quest berhasil diperbarui", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const deleteQuestMission =
  (missionId: string, handleCloseModal: () => void) =>
  async (dispatch: Dispatch, getState: any) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.deleteMission(missionId);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      const { selectedUnitId } = getState().questReducer;
      dispatch(getAllQuestMissions(selectedUnitId ? { master_unit_id: selectedUnitId } : undefined) as any);
      handleCloseModal();
      showMessage(data.message ?? "Quest berhasil dihapus", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

// ─── Checkpoints ──────────────────────────────────────────────────────────────

export const getQuestCheckpoints =
  (missionId: string) => async (dispatch: Dispatch) => {
    try {
      const data = await QuestService.getCheckpoints(missionId);
      dispatch({ type: "SET_QUEST_CHECKPOINTS", value: toArray(data, "checkpoints") });
      dispatch({ type: "SET_QUEST_SELECTED_MISSION", value: missionId });
    } catch (err: any) {
      if (err.request?.status !== 401) {
        showMessage(
          !err.response ? err.message : err.response?.data?.message ?? err.response?.data?.error,
          "error"
        );
      }
    }
  };

export const storeQuestCheckpoint =
  (missionId: string, form: QuestCheckpointPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.storeCheckpoint(missionId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestCheckpoints(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Checkpoint berhasil ditambahkan", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const updateQuestCheckpoint =
  (checkpointId: string, missionId: string, form: QuestCheckpointPayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.updateCheckpoint(checkpointId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestCheckpoints(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Checkpoint berhasil diperbarui", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const deleteQuestCheckpoint =
  (checkpointId: string, missionId: string, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.deleteCheckpoint(checkpointId);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestCheckpoints(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Checkpoint berhasil dihapus", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

// ─── Badge Tiers ──────────────────────────────────────────────────────────────

export const getQuestBadges =
  (missionId: string) => async (dispatch: Dispatch) => {
    try {
      const data = await QuestService.getBadges(missionId);
      dispatch({ type: "SET_QUEST_BADGES", value: toArray(data, "badges") });
    } catch (err: any) {
      if (err.request?.status !== 401) {
        showMessage(
          !err.response ? err.message : err.response?.data?.message ?? err.response?.data?.error,
          "error"
        );
      }
    }
  };

export const storeQuestBadge =
  (missionId: string, form: QuestBadgePayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.storeBadge(missionId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestBadges(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Badge berhasil ditambahkan", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const updateQuestBadge =
  (badgeId: string, missionId: string, form: QuestBadgePayloadTypes, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.updateBadge(badgeId, form);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestBadges(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Badge berhasil diperbarui", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };

export const deleteQuestBadge =
  (badgeId: string, missionId: string, handleCloseModal: () => void) =>
  async (dispatch: Dispatch) => {
    dispatch({ type: "SET_QUEST_FORM_LOADING", value: true });
    try {
      const data = await QuestService.deleteBadge(badgeId);
      dispatch({ type: "SET_QUEST_FORM_LOADING", value: false });
      dispatch(getQuestBadges(missionId) as any);
      handleCloseModal();
      showMessage(data.message ?? "Badge berhasil dihapus", "success");
    } catch (err: any) {
      handleError(dispatch, err);
    }
  };
